// Utilitários para formatação de moeda
import { parseOptionalNumber } from './numberGuards.ts';

const ISO_CURRENCY = /^[A-Z]{3}$/;

function safeCurrencyCode(currency?: string | null): string {
  const c = String(currency || 'BRL').trim().toUpperCase();
  return ISO_CURRENCY.test(c) ? c : 'BRL';
}

/**
 * Formata um número para o formato de moeda (Intl).
 * Moedas inválidas caem para BRL — evita RangeError que derruba a listagem.
 */
export const formatCurrency = (
  value: number,
  currency = 'BRL',
  locale = 'pt-BR'
): string => {
  const n = typeof value === 'number' ? value : Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  const code = safeCurrencyCode(currency);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
    }).format(safe);
  } catch {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(safe);
  }
};

/**
 * Remove a formatação de moeda e retorna o número em reais.
 * Aceita digitação/cola pt-BR: "4.200.000", "4.200.000,00", "4200000", "R$ 1.500,50".
 * Não assume máscara de centavos — valores grandes de aeronaves/embarcações
 * preenchidos como reais inteiros parseiam corretamente (sem /100).
 */
export const parseCurrency = (value: string, _ignoredLocale = 'pt-BR'): number => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const n = parseOptionalNumber(String(value));
  if (n === null || Number.isNaN(n)) return 0;
  return n;
};

/**
 * Formata um valor para exibição no input (sem símbolo de moeda)
 */
export const formatCurrencyInput = (value: number, locale = 'pt-BR'): string => {
  const n = typeof value === 'number' ? value : Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safe);
};

/**
 * Máscara para input de moeda em tempo real.
 *
 * - Digitação só com dígitos → trata como centavos (padrão BR: 12345 → 123,45)
 * - Valor já com vírgula decimal ou milhar com ponto → interpreta como reais
 *   (cola/fill de "4.200.000" ou "4.200.000,00" não vira 42.000)
 */
export const applyCurrencyMask = (value: string, locale = 'pt-BR'): string => {
  const raw = String(value ?? '');
  if (!raw.trim()) return '';

  const stripped = raw.replace(/[^\d.,-]/g, '');
  const hasComma = stripped.includes(',');
  const dotCount = (stripped.match(/\./g) || []).length;

  // Já veio formatado (cola, fill Playwright, blur): parse como reais
  if (hasComma || dotCount > 0) {
    const n = parseCurrency(stripped);
    if (!stripped.replace(/\D/g, '')) return '';
    return formatCurrencyInput(n, locale);
  }

  // Só dígitos: máscara clássica de centavos
  const digits = stripped.replace(/\D/g, '');
  if (!digits) return '';
  const number = parseInt(digits, 10) / 100;
  return formatCurrencyInput(number, locale);
};

export const getCurrencyLocale = (currency: string): string => {
  switch (safeCurrencyCode(currency)) {
    case 'USD':
      return 'en-US';
    case 'EUR':
      return 'de-DE';
    case 'GBP':
      return 'en-GB';
    case 'BRL':
    default:
      return 'pt-BR';
  }
};

export { safeCurrencyCode };
